import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { USER_ROLES } from '../utils/constants';
import { toast } from 'sonner';
import { Eye, EyeSlash } from '@phosphor-icons/react';

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' }, { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, signup } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    display_name: '',
    date_of_birth: '',
    state: '',
    role: USER_ROLES.GUEST,
    referral_code: searchParams.get('ref') || '',
    terms_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [dobError, setDobError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const validateAge = (dateString) => {
    if (!dateString) {
      setDobError('');
      return true;
    }
    
    const dob = new Date(dateString);
    const today = new Date();
    
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();
    
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }
    
    if (age < 18) {
      setDobError('You must be 18 years or older to create an account and use 100Nook.');
      return false;
    }
    
    setDobError('');
    return true;
  };

  const handleDobChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, date_of_birth: value });
    validateAge(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        // Validate terms acceptance
        if (!formData.terms_accepted) {
          toast.error('Please accept the Terms of Service and Privacy Policy to continue');
          setLoading(false);
          return;
        }
        
        // Validate date of birth (must be 18+)
        if (!validateAge(formData.date_of_birth)) {
          toast.error('You must be 18 years or older to create an account and use 100Nook.');
          setLoading(false);
          return;
        }
        
        await signup(
          formData.email, 
          formData.password, 
          formData.name, 
          formData.role, 
          formData.referral_code, 
          formData.display_name,
          formData.date_of_birth,
          formData.state
        );
        toast.success('Account created successfully!');
        
        // Redirect hosts to Become a Host page, guests to home
        if (formData.role === USER_ROLES.HOST) {
          navigate('/become-host');
          return;
        }
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="backdrop-blur-xl bg-[#121212] border border-white/10 p-8">
          <div className="mb-8 text-center">
            <img 
              src="/images/logo-full-transparent.png" 
              alt="100Nook - Book Private Spaces" 
              className="h-16 w-auto mx-auto mb-6"
            />
            <h1 className="text-3xl font-serif text-white mb-2">
              {isLogin ? 'Welcome Back' : 'Join 100Nook'}
            </h1>
            <p className="text-zinc-400">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name" className="text-white mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    data-testid="signup-name-input"
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-transparent border-white/20 text-white"
                    placeholder="Enter your full name"
                  />
                  <p className="text-zinc-500 text-xs mt-2">
                    Your name should match your government-issued ID. This may be verified for bookings.
                  </p>
                  <p className="text-zinc-600 text-xs mt-1">Your real name is kept private until booking is confirmed with payment.</p>
                </div>

                <div>
                  <Label htmlFor="display_name" className="text-white mb-2 block">
                    Display Name / Nickname
                  </Label>
                  <Input
                    data-testid="signup-display-name-input"
                    id="display_name"
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="bg-transparent border-white/20 text-white"
                    placeholder="How you want to be shown (e.g., 'Sarah M.')"
                  />
                  <p className="text-zinc-500 text-xs mt-1">This is what others see before booking is confirmed. Leave blank to use first name.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date_of_birth" className="text-white mb-2 block">
                      Date of Birth *
                    </Label>
                    <Input
                      data-testid="signup-dob-input"
                      id="date_of_birth"
                      type="date"
                      required
                      value={formData.date_of_birth}
                      onChange={handleDobChange}
                      className={`bg-transparent border-white/20 text-white ${dobError ? 'border-red-500' : ''}`}
                    />
                    {dobError ? (
                      <p className="text-red-500 text-xs mt-1">{dobError}</p>
                    ) : (
                      <p className="text-zinc-500 text-xs mt-1">Must be 18+</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="state" className="text-white mb-2 block">
                      State *
                    </Label>
                    <select
                      data-testid="signup-state-select"
                      id="state"
                      required
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full bg-[#121212] border border-white/20 text-white py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] rounded-md"
                    >
                      <option value="">Select state...</option>
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value}>{state.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-white mb-2 block">
                Email *
              </Label>
              <Input
                data-testid="auth-email-input"
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-transparent border-white/20 text-white"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white mb-2 block">
                Password *
              </Label>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-transparent border-white/20 text-white pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {isLogin && (
                <div className="text-right mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="role" className="text-white mb-2 block">
                  I want to
                </Label>
                <select
                  data-testid="signup-role-select"
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full bg-[#121212] border border-white/20 text-white py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                >
                  <option value={USER_ROLES.GUEST}>Book Spaces (Guest)</option>
                  <option value={USER_ROLES.HOST}>List My Space (Host)</option>
                </select>
              </div>
            )}

            {!isLogin && formData.referral_code && (
              <div className="mb-4 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                <p className="text-sm text-[#D4AF37]">✓ Referred by: {formData.referral_code}</p>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <input
                    data-testid="signup-terms-checkbox"
                    type="checkbox"
                    id="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent text-[#D4AF37] focus:ring-[#D4AF37]"
                    required
                  />
                  <label htmlFor="terms_accepted" className="text-sm text-zinc-400 leading-relaxed">
                    By signing up, I agree to 100Nook's{' '}
                    <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms of Service</Link>,{' '}
                    <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>, and{' '}
                    <Link to="/cancellation-policy" className="text-[#D4AF37] hover:underline">Cancellation Policy</Link>.
                    I also consent to receive communications from 100Nook.
                  </label>
                </div>
              </div>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading || (!isLogin && !formData.terms_accepted)}
              className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold py-3 disabled:opacity-50"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-zinc-400 hover:text-[#D4AF37] transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}